/**
 * Shared TanStack Query hooks + canonical query keys.
 * Pages MUST reuse these keys when invalidating so caches stay coherent.
 */
import { useQuery } from "@tanstack/react-query";
import type { StatsPeriod } from "@shared/types";
import * as api from "./api";

export const queryKeys = {
  auth: ["auth"] as const,
  monitors: ["monitors"] as const,
  monitor: (id: number) => ["monitor", id] as const,
  heartbeats: (id: number, limit: number) => ["heartbeats", id, limit] as const,
  monitorStats: (id: number, period: StatsPeriod) => ["monitor-stats", id, period] as const,
  monitorEvents: (id: number) => ["monitor-events", id] as const,
  events: ["events"] as const,
  groups: ["groups"] as const,
  notifications: ["notifications"] as const,
  statusPages: ["status-pages"] as const,
  statusPage: (id: number) => ["status-page", id] as const,
  publicStatus: (slug: string) => ["public-status", slug] as const,
  resolveHost: (host: string) => ["resolve-host", host] as const,
  maintenance: ["maintenance"] as const,
  settings: ["settings"] as const,
};

export function useAuthStatus() {
  return useQuery({ queryKey: queryKeys.auth, queryFn: api.getAuthStatus, staleTime: 30_000, retry: 1 });
}

/** Live monitor list — polls every 5s, drives the sidebar and dashboard. */
export function useMonitors() {
  return useQuery({ queryKey: queryKeys.monitors, queryFn: api.listMonitors, refetchInterval: 5_000 });
}

export function useGroups() {
  return useQuery({ queryKey: queryKeys.groups, queryFn: api.listGroups, staleTime: 30_000 });
}

export function useEvents(limit = 25) {
  return useQuery({
    queryKey: queryKeys.events,
    queryFn: () => api.listEvents(limit),
    refetchInterval: 10_000,
  });
}

export function useNotificationChannels() {
  return useQuery({ queryKey: queryKeys.notifications, queryFn: api.listNotifications, staleTime: 30_000 });
}

export function useStatusPages() {
  return useQuery({ queryKey: queryKeys.statusPages, queryFn: api.listStatusPages, staleTime: 30_000 });
}

export function useMaintenanceWindows() {
  return useQuery({ queryKey: queryKeys.maintenance, queryFn: api.listMaintenance, staleTime: 30_000 });
}

export function useSettings() {
  return useQuery({ queryKey: queryKeys.settings, queryFn: api.getSettings, staleTime: 60_000 });
}
